import { ReleaseHighlight, useDeleteReleaseHighlight } from "@/entities/release-highlight";
import {
    Button,
    ConfirmDialog,
    DataTable,
    DataTableColumnHeader,
    Dialog,
    DialogContainer,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/shared/components";
import { DataTableToolbarOptions } from "@/shared/components/table/data-table-toolbar";
import type { DataTableFeatures } from "@/shared/components/table/data-table-features";
import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { useState } from "react";
import { PublicatieStatusBadge } from "./publicatie-status-badge";
import { ReleaseHighlightForm } from "./release-highlight-form";

export function ReleaseHighlightsTabel({ berichten }: Readonly<{ berichten: ReleaseHighlight[] }>) {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bericht, setBericht] = useState<ReleaseHighlight | null>(null);
    const deleteReleaseHighlightMutation = useDeleteReleaseHighlight();

    const openEditDialog = (teBewerken: ReleaseHighlight | null) => {
        setBericht(teBewerken);
        setEditDialogOpen(true);
    };

    const openDeleteDialog = (teBewerken: ReleaseHighlight | null) => {
        setBericht(teBewerken);
        setDeleteDialogOpen(true);
    };

    const closeDialogs = () => {
        setEditDialogOpen(false);
        setDeleteDialogOpen(false);
        setBericht(null);
    };

    const deleteReleaseHighlight = () => {
        if (!bericht) return;
        const onSuccess = () => setDeleteDialogOpen(false);
        deleteReleaseHighlightMutation.mutate({ id: bericht.id }, { onSuccess });
    };

    const columnHelper = createColumnHelper<DataTableFeatures, ReleaseHighlight>();
    const columns = columnHelper.columns([
        columnHelper.accessor("titel", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Titel" />,
            cell: ({ row }) => (
                <span className="block truncate" title={row.original.titel}>
                    {row.original.titel}
                </span>
            ),
        }),
        columnHelper.accessor("korteOmschrijving", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Korte omschrijving" />,
            cell: ({ row }) => (
                <span className="block truncate" title={row.original.korteOmschrijving}>
                    {row.original.korteOmschrijving}
                </span>
            ),
        }),
        columnHelper.accessor("datum", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Publicatiedatum" />,
            cell: ({ row }) => format(row.original.datum, "dd-MM-yyyy HH:mm", { locale: nl }),
        }),
        columnHelper.display({
            id: "status",
            header: "Status",
            cell: ({ row }) => <PublicatieStatusBadge bericht={row.original} />,
        }),
        columnHelper.display({
            id: "acties",
            header: "Acties",
            cell: ({ row }) => (
                <div className="flex flex-row items-center gap-2">
                    <Button
                        variant="tertiary"
                        iconOnly
                        icon="edit"
                        title="Bewerken"
                        aria-label={`Bewerk ${row.original.titel}`}
                        onClick={() => openEditDialog(row.original)}
                    />
                    <Button
                        variant="tertiary"
                        iconOnly
                        icon="trash"
                        title="Verwijderen"
                        aria-label={`Verwijder ${row.original.titel}`}
                        onClick={() => openDeleteDialog(row.original)}
                    />
                </div>
            ),
        }),
    ]);

    const toolbarOptions: DataTableToolbarOptions<ReleaseHighlight> = {
        actions: [
            {
                label: "Nieuw bericht",
                onClick: () => openEditDialog(null),
                "data-testid": "release-highlight-nieuw",
            },
        ],
    };

    return (
        <div className="min-w-0" data-testid="release-highlights-tabel">
            <DataTable
                columns={columns}
                data={berichten}
                toolbarOptions={toolbarOptions}
                onDoubleClick={(row) => openEditDialog(row.original)}
            />

            <Dialog open={editDialogOpen} onOpenChange={(open) => (open ? setEditDialogOpen(true) : closeDialogs())}>
                <DialogContainer
                    className="grid-rows-[auto_minmax(0,1fr)] overflow-hidden sm:max-w-4xl"
                    data-testid="release-highlight-beheer-dialog"
                >
                    <DialogHeader>
                        <DialogTitle>{bericht ? "Bericht bewerken" : "Nieuw bericht"}</DialogTitle>
                        <DialogDescription>Schrijf de tekst in markdown; met de voorbeeldknop zie je hoe het eruitziet.</DialogDescription>
                    </DialogHeader>
                    <ReleaseHighlightForm key={bericht?.id ?? "nieuw"} bericht={bericht} onSaved={closeDialogs} onCancel={closeDialogs} />
                </DialogContainer>
            </Dialog>
            <ConfirmDialog
                key={bericht?.id ?? "none"}
                destructive
                isOpen={deleteDialogOpen}
                onCancel={closeDialogs}
                onConfirm={deleteReleaseHighlight}
                isPending={deleteReleaseHighlightMutation.isPending}
                error={deleteReleaseHighlightMutation.error}
                title="Verwijder release highlight"
                confirmLabel="Verwijderen"
                cancelLabel="Annuleren"
            >
                Weet je zeker dat je de release highlight met titel "{bericht?.titel}" wil verwijderen?
            </ConfirmDialog>
        </div>
    );
}
